import { ref } from 'vue';
import { IDictionaryItem, IMapData, IDictDictListResItem } from './types';
import { dictDictList } from './api';
import { cloneDeep } from 'lodash-es';
import { ElMessage } from 'element-plus';
import { DICT_STATE_ENUM } from './constant';
import {
  Dictionary,
  IDictionaryFindByCodeOptions,
  IDictionaryFindByDataOptions,
  IDictionaryQueryOptions
} from '@toy/v-use';

const dictMap = ref<IMapData>({});
const taskQueue = new Map<string, Array<(val: IDictionaryItem[]) => void>>();
let timer: any = 0;

// 结构转化
const deepConvertTree = (data: IDictDictListResItem[]) => {
  return data.map((item) => {
    const newItem: IDictionaryItem = {
      ...item,
      label: item.dictName,
      value: item.dictCode,
      disabled: item.state === DICT_STATE_ENUM.DISABLED,
    };
    if (item.children && item.children.length > 0) {
      newItem.children = deepConvertTree(item.children);
    }
    return newItem;
  });
};
// 根据dictCode查找字典
const deepSearchByDictCode = (data: IDictionaryItem[], value: string): IDictionaryItem | null => {
  for (let index = 0; index < data.length; index++) {
    const item = data[index];
    if (item.dictCode === value) {
      return item;
    }
    if (item.children && item.children.length > 0) {
      const result = deepSearchByDictCode(item.children!, value);
      if (result) return result;
    }
  }
  return null;
};
export const useDictionary = () => {
  const getData = async () => {
    try {
      if (taskQueue.size === 0) return;
      const keys = [...taskQueue.keys()];
      const { data } = await dictDictList({
        dictCodes: keys,
      });
      const newData = deepConvertTree(data);
      keys.forEach((key) => {
        const dictArr = newData.find(it => it.dictCode === key)?.children || [];
        dictMap.value[key] = dictArr || [];
        const cbs = taskQueue.get(key!) || [];
        cbs.forEach(cb => cb(dictArr));
        taskQueue.delete(key!);
      });
    } catch (e) {
      console.error(e, '获取字典数据接口异常');
    }
  };
  const clearTask = () => {
    if (timer) {
      clearTimeout(timer);
      timer = 0;
    }
  };
  const createTask = () => {
    clearTask();
    if (taskQueue.size === 0) return;
    timer = setTimeout(() => {
      getData();
    }, 100);
  };
  const getDictionary = (
    code: string,
    cb?: (val: IDictionaryItem[]) => void
  ) => {
    if (dictMap.value[code]) {
      cb?.(dictMap.value[code]);
      return;
    }
    const cur = taskQueue.get(code);
    if (cur) {
      if (cb) {
        cur.push(cb);
      }
    } else {
      taskQueue.set(code, cb ? [cb] : []);
    }
    createTask();
  };

  /**
   * @param key  过滤数据
   * @param maxLevel 最大层级 默认不做限制
   * @param minLevel 最小层级 默认不做限制
   */
  const handleFilterData = (data: IDictionaryItem[], minLevel?: number) => {
    if (!data.length || !minLevel) return data;
    const copyData = cloneDeep<IDictionaryItem[]>(data);
    const deep = (arr: IDictionaryItem[]) => {
      for (let index = 0; index < arr.length; index++) {
        const item = arr[index];
        // 最小级
        if (minLevel) {
          if (item?.children?.length) {
            deep(item?.children);
          } else if (Number(item.level) < minLevel!) {
            // 最后一级如果小于minLevel，全部过滤掉
            arr.splice(index, 1);
            // 如果该级为空，则父级一层层删除
            if (!arr?.length) {
              deep(copyData);
            }
            index -= 1;
          }
        }
      }
    };
    deep(copyData);
    return copyData;
  };
  /**
   * @param key 字典编码
   * @param maxLevel 最大层级 默认不做限制
   * @param minLevel 最小层级 默认不做限制
   */
  const getDictionaryOptions = (key: string, maxLevel?: number, minLevel?: number) => {
    if (!key) return [];
    getDictionary(key);
    const data = dictMap.value[key] || [];
    if (!maxLevel && !minLevel) return data;
    if (maxLevel && minLevel) {
      ElMessage.warning('不支持同时取最大最小级');
      return data;
    }
    const deep = (arr: IDictionaryItem[], currentLevel: number = 1): IDictionaryItem[] => {
      return arr.map((v) => {
        if (v?.children?.length) {
          // 最大层级
          if (maxLevel) {
            if (currentLevel === maxLevel) {
              const { ...rest } = v;
              return {
                ...rest,
                level: currentLevel,
                children: [],
              };
            }
          }
          return {
            ...v,
            level: currentLevel,
            children: deep(v?.children, currentLevel + 1),
          };
        }
        return {
          ...v,
          level: currentLevel
        };
      });
    };
    return handleFilterData(deep(data), minLevel);
  };
  /**
   * @param key 字典编码
   * @param maxLevel 最大层级 默认不做限制
   * @param minLevel 最小层级 默认不做限制
   */
  const getDictionaryOptionsSync = (key: string, maxLevel?: number, minLevel?: number) => {
    return new Promise<IDictionaryItem[]>((resolve) => {
      if (!key) {
        resolve([]);
        return;
      }
      getDictionary(key, (val) => {
        if (!maxLevel && !minLevel) {
          resolve(val);
          return;
        }
        if (maxLevel && minLevel) {
          ElMessage.warning('不支持同时取最大最小级');
          resolve(val);
          return;
        }
        const deep = (arr: IDictionaryItem[], currentLevel: number = 1): IDictionaryItem[] => {
          return arr.map((v) => {
            if (v?.children?.length) {
              // 最大层级
              if (maxLevel) {
                if (currentLevel === maxLevel) {
                  const { ...rest } = v;
                  return {
                    ...rest,
                    level: currentLevel,
                    children: [],
                  };
                }
              }
              return {
                ...v,
                level: currentLevel,
                children: deep(v?.children, currentLevel + 1),
              };
            }
            return {
              ...v,
              level: currentLevel
            };
          });
        };
        resolve(handleFilterData(deep(val), minLevel));
      });
    });
  };

  /**
   * 仅获取可用字典
   * @param key
   * @param maxLevel
   * @param minLevel
   */
  const getEnableDictionaryOptions = (key: string, maxLevel?: number, minLevel?: number) => {
    if (!key) return [];
    const originData = getDictionaryOptions(key, maxLevel, minLevel);
    const filterData = (data: IDictionaryItem[]): IDictionaryItem[] => {
      const target: IDictionaryItem[] = data.filter(i => !i.disabled);
      return target.map((item) => {
        return {
          ...item,
          children: filterData(item.children || []),
        };
      });
    };
    return filterData(originData);
  };
  /**
   * 仅获取可用字典
   * @param key
   * @param maxLevel
   * @param minLevel
   */
  const getEnableDictionaryOptionsSync = async (key: string, maxLevel?: number, minLevel?: number) => {
    if (!key) return [];
    const originData = await getDictionaryOptionsSync(key, maxLevel, minLevel);
    const filterData = (data: IDictionaryItem[]) => {
      const target: IDictionaryItem[] = data.filter(i => !i.disabled);
      target.forEach((item) => {
        if (item.children?.length) {
          item.children = filterData(item.children);
        }
      });
      return target;
    };
    return filterData(originData);
  };

  const convertValueToLabel = (key: string, code: string) => {
    const originData = getDictionaryOptions(key);
    if (originData.length === 0) return '';
    const find = deepSearchByDictCode(originData, code);

    return find?.label || '';
  };
  return {
    getDictionaryOptions,
    getDictionaryOptionsSync,
    getEnableDictionaryOptions,
    getEnableDictionaryOptionsSync,
    convertValueToLabel,
  };
};

const dictionary = new Dictionary({
  apiOptions: {
    consumerCode: 'AIAP',
  },
});

/**
 * 异步获取字典选项数据
 * @param key 字典编码，用于标识特定的字典数据
 * @param data 字典查询选项，可包含层级限制等查询条件
 * @returns 返回Promise类型的字典数据结果
 */
export const getDictionaryOptions = (key: string, data?: IDictionaryQueryOptions) => {
  return dictionary.get(key, data);
};

/**
 * 同步获取字典选项数据（注：实际实现与异步方法相同）
 * @param key 字典编码，用于标识特定的字典数据
 * @param data 字典查询选项，可包含层级限制等查询条件
 * @returns 返回Promise类型的字典数据结果
 */
export const getDictionaryOptionsSync = (key: string, data?: IDictionaryQueryOptions) => {
  return dictionary.getSync(key, data);
};

/**
 * 批量同步获取字典选项数据
 * @param keys 字典编码数组，用于标识多个特定的字典数据
 * @param data 字典查询选项，可包含层级限制等查询条件
 * @returns 返回Promise类型的批量字典数据结果
 */
export const batchDictionaryOptionsSync = (keys: string[], data?: IDictionaryQueryOptions) => {
  return dictionary.batchSync(keys, data);
};

/**
 * 根据字典编码在指定字典中查找数据
 * @param codes 需要查找的字典项编码数组
 * @param data 查找配置选项
 * @param data.dictCode 目标字典的编码，指定在哪个字典中进行查找
 * @param data.deep 是否深度查询子级数据，true表示递归查找所有层级的子数据
 * @returns 返回匹配的字典项数据数组
 */
export const findByCodeDictionarySync = (codes: string[], data: IDictionaryFindByCodeOptions) => {
  return dictionary.findByCode(codes, data);
};

/**
 * 在给定的字典数据中根据编码查找数据
 * @param codes 需要查找的字典项编码数组
 * @param sourceData 查找配置选项
 * @param sourceData.dictData 用于查询的字典数据源，这是一个已经获取到的字典数据数组
 * @param sourceData.deep 是否深度查询子级数据，true表示递归查找所有层级的子数据
 * @returns 返回匹配的字典项数据数组
 */
export const findByDataDictionary = (codes: string[], sourceData: IDictionaryFindByDataOptions) => {
  return dictionary.findByData(codes, sourceData);
};
